import { useOne, useCustom, useCustomMutation, useInvalidate, useList, useUpdate } from '@refinedev/core';
import { useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  Copy,
  ExternalLink,
  RefreshCw,
  XCircle,
  CalendarClock,
  Square,
  Users,
  RadioTower,
  Check,
  Film,
  VolumeX,
  Ban,
  BarChart3,
  MessageSquareText,
  Plus,
  Activity,
  Gauge,
  Wifi,
  Bell,
  Mail,
  Send,
  Trash2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  LivestreamStatus,
  LivestreamVisibility,
  NotificationChannel,
  type LivestreamNotificationButton,
  type LivestreamNotificationRuleDto,
  type LivestreamNotificationSettingsDto,
  type LivestreamNotificationSubscriberDto,
} from '@masters-club/types';
import { notify } from '@/lib/notify';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import { FormHeader } from '@/components/forms';
import {
  type Livestream,
  LIVESTREAM_STATUS_META,
  LIVESTREAM_VISIBILITY_LABELS,
  DEFAULT_RTMP_URL,
  getLivestreamTitle,
  getLivestreamDescription,
  type LivestreamFormData,
} from './types';
import { LivestreamsFormFields } from './form';
import { LivestreamCallStage } from './components/livestream-call-stage';
import { LivestreamBreakoutPanel } from './components/livestream-breakout-panel';
import { useLivestreamRealtime } from './hooks/use-livestream-realtime';
import { ChatModerationPanel } from '../moderation/components/chat-tab';
import type { ChatRoomWithStats } from '../moderation/components/chat-types';
import { InteractionsAdmin } from './components/interactions-admin';
import { ObsDiagnosticsPanel } from './components/ObsDiagnosticsPanel';
import { StreamSourcePanel } from './components/StreamSourcePanel';
import { RestreamTab } from './components/RestreamTab';
import { TranslationTab } from './components/TranslationTab';
import { LivestreamViewersPanel } from './components/LivestreamViewersPanel';
import { LivestreamAudiencePanel } from './components/LivestreamAudiencePanel';
import type { TranslationLanguage } from '@/components/translations';

interface DocumentPictureInPictureOptions {
  width?: number;
  height?: number;
  disallowReturnToOpener?: boolean;
}

interface DocumentPictureInPicture {
  requestWindow(options?: DocumentPictureInPictureOptions): Promise<Window>;
}

declare global {
  interface Window {
    documentPictureInPicture?: DocumentPictureInPicture;
  }
}

interface ActiveViewer {
  userId: number;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  joinedAt: Date | string;
  watchedSeconds: number;
  isMuted: boolean;
  mutedUntil: Date | string | null;
  isBanned: boolean;
  banReason: string | null;
}

interface LanguageRecord {
  code: string;
  name: string;
  nativeName: string;
  flagEmoji?: string | null;
  flag?: string | null;
}

interface LivestreamAnalyticsBucket {
  bucketStart: string;
  bucketEnd: string;
  activeViewers: number;
  joins: number;
  leaves: number;
  viewerSeconds: number;
  estimatedTrafficMb: number;
  chatMessages: number;
  viewerMessages: number;
  adminMessages: number;
  interactionResponses: number;
  moderationActions: number;
}

interface LivestreamMediaRuntime {
  available: boolean;
  path: string;
  sourceReady: boolean;
  activeReaders: number;
  tracks: number;
  bytesReceived: number;
  bytesSent: number;
  readyTime: string | null;
  snapshotAt: string;
}

interface LivestreamNetworkCapacity {
  available: boolean;
  iface: string | null;
  nicSpeedMbps: number | null;
  currentTxMbps: number | null;
  freeMbps: number | null;
  bitratePerViewerMbps: number | null;
  capacityViewers: number | null;
  activeViewers: number;
}

interface LivestreamAnalyticsResponse {
  bucketMinutes: number;
  averageBitrateMbps: number;
  window: {
    startAt: string;
    endAt: string;
  };
  mediaRuntime: LivestreamMediaRuntime;
  summary: {
    peakViewers: number;
    totalUniqueViewers: number;
    totalViewerSeconds: number;
    totalEstimatedTrafficMb: number;
    totalChatMessages: number;
    totalInteractionResponses: number;
    totalModerationActions: number;
    averageViewers: number;
    chatMessagesPerViewerHour: number;
  };
  buckets: LivestreamAnalyticsBucket[];
}

interface LivestreamAnalyticsChartPoint extends LivestreamAnalyticsBucket {
  label: string;
}

interface LivestreamAnalyticsTooltipPayload {
  dataKey: keyof LivestreamAnalyticsBucket;
  name?: string;
  value: number;
  color?: string;
}

interface LivestreamAnalyticsTooltipProps {
  active?: boolean;
  payload?: LivestreamAnalyticsTooltipPayload[];
  label?: string;
  data: LivestreamAnalyticsChartPoint[];
}

const LIVESTREAM_CHAT_WINDOW_WIDTH = 860;
const LIVESTREAM_CHAT_WINDOW_HEIGHT = 760;

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
}

interface CustomQueryData<T> {
  data: T | ApiEnvelope<T>;
}

function isApiEnvelope<T>(payload: T | ApiEnvelope<T>): payload is ApiEnvelope<T> {
  return typeof payload === 'object' && payload !== null && 'success' in payload && 'data' in payload;
}

function getCustomData<T>(queryData: CustomQueryData<T> | undefined, fallback: T): T {
  if (!queryData) {
    return fallback;
  }
  return isApiEnvelope(queryData.data) ? queryData.data.data : queryData.data;
}

function formatWatchTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

const dateFormatter = new Intl.DateTimeFormat('ru-RU', {
  dateStyle: 'long',
  timeStyle: 'short',
});

function formatDateTime(value: string | null): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : dateFormatter.format(date);
}

function formatShortDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(value);
}

function formatMegabytes(value: number): string {
  if (value >= 1024) {
    return `${formatNumber(value / 1024)} ГБ`;
  }
  return `${formatNumber(value)} МБ`;
}

function formatBytes(value: number): string {
  const megabytes = value / 1024 / 1024;
  return formatMegabytes(megabytes);
}

async function copyToClipboard(text: string, label: string) {
  try {
    await navigator.clipboard.writeText(text);
    notify.success(`${label} скопирован в буфер обмена`);
  } catch {
    notify.error('Не удалось скопировать');
  }
}

function StatusBadge({ status }: { status: LivestreamStatus }) {
  const meta = LIVESTREAM_STATUS_META[status];
  return (
    <Badge variant={meta.variant} className="gap-1.5 font-normal">
      <span className={`inline-block h-2 w-2 rounded-full ${meta.dotClass}`} />
      {meta.label}
    </Badge>
  );
}

function openLivestreamChatOverlay(livestreamId: number): Window | null {
  const width = LIVESTREAM_CHAT_WINDOW_WIDTH;
  const height = LIVESTREAM_CHAT_WINDOW_HEIGHT;
  const left = Math.max(0, window.screenX + window.outerWidth - width - 24);
  const top = Math.max(0, window.screenY + 72);

  const chatWindow = window.open(
    `/livestreams/chat-overlay/${livestreamId}`,
    `livestream-chat-${livestreamId}`,
    `popup=yes,width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=no`,
  );

  if (!chatWindow) {
    notify.error('Не удалось открыть окно чата. Проверьте блокировку всплывающих окон.');
  }

  return chatWindow;
}

function copyDocumentStyles(targetDocument: Document): () => void {
  const styleNodes = [...document.querySelectorAll('link[rel="stylesheet"], style')];
  const copiedNodes = styleNodes.map((styleNode) => styleNode.cloneNode(true) as ChildNode);

  for (const copiedNode of copiedNodes) {
    targetDocument.head.append(copiedNode);
  }

  return () => {
    for (const copiedNode of copiedNodes) {
      copiedNode.remove();
    }
  };
}

function LivestreamChatPictureInPicture({
  children,
  pipWindow,
  title,
}: {
  children: (portalContainer: HTMLElement) => ReactNode;
  pipWindow: Window | null;
  title: string;
}) {
  const [container, setContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!pipWindow || pipWindow.closed) {
      setContainer(null);
      return;
    }

    const targetDocument = pipWindow.document;
    targetDocument.title = title;
    targetDocument.documentElement.className = document.documentElement.className;
    targetDocument.body.innerHTML = '';
    targetDocument.body.className = 'min-h-screen overflow-hidden bg-background text-foreground';

    const cleanupStyles = copyDocumentStyles(targetDocument);
    const root = targetDocument.createElement('div');
    root.className = 'h-screen overflow-hidden bg-background p-3 text-foreground';
    targetDocument.body.append(root);
    setContainer(root);

    return () => {
      setContainer(null);
      cleanupStyles();
      root.remove();
    };
  }, [pipWindow, title]);

  if (!container) {
    return null;
  }

  return createPortal(children(container.ownerDocument.body), container);
}

function LivestreamChatModeration({
  livestreamId,
  chatRoomId,
  overlay = false,
}: {
  livestreamId: number;
  chatRoomId: number | null;
  overlay?: boolean;
}) {
  const [pictureInPictureWindow, setPictureInPictureWindow] = useState<Window | null>(null);
  const [isOpeningPictureInPicture, setIsOpeningPictureInPicture] = useState(false);
  const fallbackWindowRef = useRef<Window | null>(null);
  const chatRoomQuery = useCustom<{ data: ChatRoomWithStats }>({
    url: `/admin/chat-moderation/rooms/${chatRoomId ?? 0}`,
    method: 'get',
    queryOptions: {
      enabled: chatRoomId !== null,
    },
  });

  const room = chatRoomQuery.query.data?.data.data ?? null;
  const isLoading = chatRoomQuery.query.isLoading;
  const hasChat = chatRoomId !== null;
  const isPictureInPictureOpen = pictureInPictureWindow !== null && !pictureInPictureWindow.closed;

  const closePictureInPicture = useCallback(() => {
    if (pictureInPictureWindow && !pictureInPictureWindow.closed) {
      pictureInPictureWindow.close();
    }

    setPictureInPictureWindow(null);
  }, [pictureInPictureWindow]);

  const openDetachedChat = useCallback(async () => {
    if (overlay) {
      window.close();
      return;
    }

    if (isPictureInPictureOpen) {
      pictureInPictureWindow.focus();
      return;
    }

    const documentPictureInPicture = window.documentPictureInPicture;

    if (!documentPictureInPicture) {
      fallbackWindowRef.current = openLivestreamChatOverlay(livestreamId);
      fallbackWindowRef.current?.focus();
      return;
    }

    setIsOpeningPictureInPicture(true);

    try {
      const nextWindow = await documentPictureInPicture.requestWindow({
        width: LIVESTREAM_CHAT_WINDOW_WIDTH,
        height: LIVESTREAM_CHAT_WINDOW_HEIGHT,
        disallowReturnToOpener: false,
      });

      nextWindow.addEventListener('pagehide', () => setPictureInPictureWindow(null), { once: true });
      setPictureInPictureWindow(nextWindow);
      nextWindow.focus();
    } catch {
      fallbackWindowRef.current = openLivestreamChatOverlay(livestreamId);
      fallbackWindowRef.current?.focus();
    } finally {
      setIsOpeningPictureInPicture(false);
    }
  }, [isPictureInPictureOpen, livestreamId, overlay, pictureInPictureWindow]);

  useEffect(() => () => {
    if (pictureInPictureWindow && !pictureInPictureWindow.closed) {
      pictureInPictureWindow.close();
    }
  }, [pictureInPictureWindow]);

  if (!hasChat) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground">
        Чат ещё не создан для этой трансляции.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[420px] items-center justify-center rounded-lg border text-sm text-muted-foreground">
        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
        Загружаем чат...
      </div>
    );
  }

  if (!room) {
    return (
      <div className="rounded-lg border border-destructive/30 p-6 text-sm text-destructive">
        Не удалось загрузить комнату чата.
      </div>
    );
  }

  let detachedChatButtonLabel = 'В отдельное окно';
  if (overlay) {
    detachedChatButtonLabel = 'Закрыть';
  } else if (isPictureInPictureOpen) {
    detachedChatButtonLabel = 'Закрыть PiP';
  } else if (isOpeningPictureInPicture) {
    detachedChatButtonLabel = 'Открываем...';
  }

  return (
    <>
      <ChatModerationPanel
        room={room}
        contentHeightClassName={overlay ? 'h-[calc(100vh-150px)]' : 'h-[calc(100vh-360px)] min-h-[560px]'}
        showParticipants={!overlay}
        headerActions={(
          <Button
            variant="outline"
            size="sm"
            onClick={isPictureInPictureOpen ? closePictureInPicture : openDetachedChat}
            disabled={isOpeningPictureInPicture}
          >
            {overlay || isPictureInPictureOpen ? <XCircle /> : <ExternalLink />}
            {detachedChatButtonLabel}
          </Button>
        )}
      />

      <LivestreamChatPictureInPicture
        pipWindow={pictureInPictureWindow}
        title="Чат трансляции"
      >
        {(portalContainer) => (
          <ChatModerationPanel
            room={room}
            contentHeightClassName="h-[calc(100vh-130px)]"
            showParticipants={false}
            menuPortalContainer={portalContainer}
            headerActions={(
              <Button variant="outline" size="sm" onClick={closePictureInPicture}>
                <XCircle />
                Закрыть PiP
              </Button>
            )}
          />
        )}
      </LivestreamChatPictureInPicture>
    </>
  );
}

interface LocalizedTemplateForm {
  title: string;
  messageTemplate: string;
  buttonText: string;
  buttonUrl: string;
}

interface LivestreamNotificationRuleForm {
  localId: string;
  serverId: number | null;
  minutesBefore: string;
  channels: NotificationChannel[];
  title: string;
  messageTemplate: string;
  buttonText: string;
  buttonUrl: string;
  localizedTemplates: Record<string, LocalizedTemplateForm>;
  isActive: boolean;
  sentAt: string | null;
  lastError: string | null;
}

const NOTIFICATION_LANGUAGES: Array<{ code: string; label: string }> = [
  { code: 'ru', label: 'Русский' },
  { code: 'en', label: 'English' },
  { code: 'uz', label: "O'zbekcha" },
];

const LIVESTREAM_NOTIFICATION_CHANNEL_OPTIONS: Array<{
  value: NotificationChannel;
  label: string;
  icon: ReactNode;
}> = [
  { value: NotificationChannel.TELEGRAM, label: 'Telegram', icon: <Send className="h-3.5 w-3.5" /> },
  { value: NotificationChannel.EMAIL, label: 'Email', icon: <Mail className="h-3.5 w-3.5" /> },
];

// Подписи каналов для ОТОБРАЖЕНИЯ (вкл. легаси APP, который убран из выбора,
// но может остаться в channels у старых правил). Не используется для выбора.
const LIVESTREAM_NOTIFICATION_CHANNEL_LABELS: Record<NotificationChannel, string> = {
  [NotificationChannel.TELEGRAM]: 'Telegram',
  [NotificationChannel.EMAIL]: 'Email',
  [NotificationChannel.APP]: 'Платформа',
};

const DEFAULT_LIVESTREAM_NOTIFICATION_TITLE = 'Скоро эфир';
const DEFAULT_LIVESTREAM_NOTIFICATION_MESSAGE = '<b>{{livestream.title}}</b>\n\nНачинаем {{livestream.scheduledAtText}}. Нажмите кнопку ниже, чтобы открыть трансляцию.';

const MINUTES_BEFORE_PRESETS: Array<{ value: string; label: string }> = [
  { value: '15', label: '15 минут' },
  { value: '30', label: '30 минут' },
  { value: '60', label: '1 час' },
  { value: '120', label: '2 часа' },
  { value: '360', label: '6 часов' },
  { value: '1440', label: '1 день' },
];

const MINUTES_BEFORE_PRESET_VALUES = new Set(MINUTES_BEFORE_PRESETS.map((preset) => preset.value));

function formatMinutesBefore(minutes: string): string {
  const num = Number(minutes);
  if (!Number.isFinite(num) || num < 0) return `${minutes} мин.`;
  if (num === 0) return 'В момент старта';
  if (num < 60) return `${num} мин.`;
  if (num % 1440 === 0) {
    const days = num / 1440;
    return days === 1 ? '1 день' : `${days} дн.`;
  }
  if (num % 60 === 0) {
    const hours = num / 60;
    return hours === 1 ? '1 час' : `${hours} ч.`;
  }
  return `${num} мин.`;
}

let livestreamNotificationDraftSequence = 0;

function createLivestreamNotificationDraftId(): string {
  livestreamNotificationDraftSequence += 1;
  return `draft-${livestreamNotificationDraftSequence}`;
}

function createDefaultLivestreamNotificationRule(minutesBefore = 15): LivestreamNotificationRuleForm {
  return {
    localId: createLivestreamNotificationDraftId(),
    serverId: null,
    minutesBefore: String(minutesBefore),
    channels: [NotificationChannel.TELEGRAM],
    title: DEFAULT_LIVESTREAM_NOTIFICATION_TITLE,
    messageTemplate: DEFAULT_LIVESTREAM_NOTIFICATION_MESSAGE,
    buttonText: 'Открыть трансляцию',
    buttonUrl: '',
    localizedTemplates: {},
    isActive: true,
    sentAt: null,
    lastError: null,
  };
}

function mapNotificationRuleToForm(rule: LivestreamNotificationRuleDto): LivestreamNotificationRuleForm {
  const localizedTemplates: Record<string, LocalizedTemplateForm> = {};
  if (rule.localizedTemplates) {
    for (const [lang, tpl] of Object.entries(rule.localizedTemplates)) {
      localizedTemplates[lang] = {
        title: tpl.title,
        messageTemplate: tpl.messageTemplate,
        buttonText: tpl.button?.text ?? '',
        buttonUrl: tpl.button?.url ?? '',
      };
    }
  }
  return {
    localId: `saved-${rule.id}`,
    serverId: rule.id,
    minutesBefore: String(rule.minutesBefore),
    channels: rule.channels,
    title: rule.title,
    messageTemplate: rule.messageTemplate,
    buttonText: rule.button?.text ?? '',
    buttonUrl: rule.button?.url ?? '',
    localizedTemplates,
    isActive: rule.isActive,
    sentAt: rule.sentAt,
    lastError: rule.lastError,
  };
}

function normalizeLivestreamNotificationButton(
  buttonText: string,
  buttonUrl: string,
): LivestreamNotificationButton | null {
  const text = buttonText.trim();
  const url = buttonUrl.trim();

  if (!text && !url) {
    return null;
  }
  if (!text || !url) {
    throw new Error('Для кнопки заполните и текст, и ссылку');
  }

  new URL(url);
  return { text, url };
}

function getLivestreamNotificationChannelsLabel(channels: NotificationChannel[]): string {
  // Берём подписи из label-карты (а не из массива выбора), чтобы легаси APP
  // тоже отобразился у старых правил, хотя из выбора он убран.
  return channels
    .map((ch) => LIVESTREAM_NOTIFICATION_CHANNEL_LABELS[ch])
    .filter(Boolean)
    .join(', ');
}

function LivestreamNotificationSubscribersDialog({
  subscribers,
  subscribersCount,
}: {
  subscribers: LivestreamNotificationSubscriberDto[];
  subscribersCount: number;
}) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          type="button"
          className="rounded-lg border bg-background px-3 py-2 text-left transition-colors hover:bg-muted/50"
        >
          <div className="text-[11px] uppercase tracking-normal text-muted-foreground">Подписались</div>
          <div className="text-lg font-semibold tabular-nums">{subscribersCount}</div>
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Подписчики уведомлений</DialogTitle>
          <DialogDescription>
            Пользователи, которым будут отправлены правила из этой вкладки.
          </DialogDescription>
        </DialogHeader>
        {subscribers.length === 0 ? (
          <div className="rounded-lg border border-dashed bg-muted/30 p-6 text-center text-sm text-muted-foreground">
            Пока никто не подписался на уведомления об этом эфире.
          </div>
        ) : (
          <div className="max-h-[420px] overflow-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Пользователь</TableHead>
                  <TableHead>Контакты</TableHead>
                  <TableHead>Источник</TableHead>
                  <TableHead>Подписка</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subscribers.map((subscriber) => (
                  <TableRow key={subscriber.userId}>
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="font-medium">{subscriber.displayName}</span>
                        <span className="text-xs text-muted-foreground">ID {subscriber.userId}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-0.5 text-xs text-muted-foreground">
                        {subscriber.username && <span>@{subscriber.username}</span>}
                        {subscriber.email && <span>{subscriber.email}</span>}
                        {subscriber.telegramId && <span>TG {subscriber.telegramId}</span>}
                        {!subscriber.username && !subscriber.email && !subscriber.telegramId && <span>—</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={subscriber.source === 'AUTO' ? 'secondary' : 'outline'}>
                        {subscriber.source === 'AUTO' ? 'Авто' : 'Вручную'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDateTime(subscriber.subscribedAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LivestreamNotificationsAdmin({ livestream }: { livestream: Livestream }) {
  const [rules, setRules] = useState<LivestreamNotificationRuleForm[]>([]);
  const [collapsedRuleIds, setCollapsedRuleIds] = useState<string[]>([]);
  const settingsQuery = useCustom<ApiEnvelope<LivestreamNotificationSettingsDto> | LivestreamNotificationSettingsDto>({
    url: `/admin/livestreams/${livestream.id}/notifications`,
    method: 'get',
  });
  const saveMutation = useCustomMutation();
  const settings = getCustomData<LivestreamNotificationSettingsDto | null>(settingsQuery.query.data, null);
  const isScheduled = livestream.status === LivestreamStatus.SCHEDULED;
  const isSaving = saveMutation.mutation.isPending;

  useEffect(() => {
    if (!settings) {
      return;
    }
    const rulesFromServer = settings.rules ?? [];
    setRules(
      rulesFromServer.length > 0
        ? rulesFromServer.map(mapNotificationRuleToForm)
        : [createDefaultLivestreamNotificationRule()],
    );
    setCollapsedRuleIds([]);
  }, [settings]);

  const updateRule = (
    localId: string,
    updater: (rule: LivestreamNotificationRuleForm) => LivestreamNotificationRuleForm,
  ) => {
    setRules((currentRules) => currentRules.map((rule) => (rule.localId === localId ? updater(rule) : rule)));
  };

  const updateLocalizedField = (
    localId: string,
    lang: string,
    field: keyof LocalizedTemplateForm,
    value: string,
  ) => {
    updateRule(localId, (rule) => {
      const existing = rule.localizedTemplates[lang] ?? { title: '', messageTemplate: '', buttonText: '', buttonUrl: '' };
      return {
        ...rule,
        localizedTemplates: {
          ...rule.localizedTemplates,
          [lang]: { ...existing, [field]: value },
        },
      };
    });
  };

  const toggleChannel = (localId: string, channel: NotificationChannel) => {
    updateRule(localId, (rule) => {
      const hasChannel = rule.channels.includes(channel);
      const nextChannels = hasChannel
        ? rule.channels.filter((item) => item !== channel)
        : [...rule.channels, channel];
      return { ...rule, channels: nextChannels };
    });
  };

  const removeRule = (localId: string) => {
    setRules((currentRules) => currentRules.filter((rule) => rule.localId !== localId));
    setCollapsedRuleIds((currentIds) => currentIds.filter((id) => id !== localId));
  };

  const addRule = () => {
    const rule = createDefaultLivestreamNotificationRule();
    setRules((currentRules) => [...currentRules, rule]);
    setCollapsedRuleIds((currentIds) => currentIds.filter((id) => id !== rule.localId));
  };

  const toggleRuleCollapsed = (localId: string) => {
    setCollapsedRuleIds((currentIds) => (
      currentIds.includes(localId)
        ? currentIds.filter((id) => id !== localId)
        : [...currentIds, localId]
    ));
  };

  const handleSave = () => {
    try {
      const normalizedRules = rules.map((rule) => {
        const minutesBefore = Number.parseInt(rule.minutesBefore, 10);
        if (!Number.isInteger(minutesBefore) || minutesBefore < 0 || minutesBefore > 10080) {
          throw new Error('Укажите время уведомления от 0 до 10080 минут (7 дней)');
        }
        if (rule.channels.length === 0) {
          throw new Error('Выберите хотя бы один канал отправки');
        }
        const title = rule.title.trim();
        const messageTemplate = rule.messageTemplate.trim();
        if (!title || !messageTemplate) {
          throw new Error('Заполните заголовок и текст уведомления');
        }

        const localizedTemplates: Record<string, { title: string; messageTemplate: string; button: LivestreamNotificationButton | null }> = {};
        for (const [lang, tpl] of Object.entries(rule.localizedTemplates)) {
          const langTitle = tpl.title.trim();
          const langMessage = tpl.messageTemplate.trim();
          if (!langTitle && !langMessage && !tpl.buttonText.trim()) continue;
          if (langTitle && langMessage) {
            localizedTemplates[lang] = {
              title: langTitle,
              messageTemplate: langMessage,
              button: normalizeLivestreamNotificationButton(tpl.buttonText, tpl.buttonUrl),
            };
          }
        }

        return {
          id: rule.serverId ?? undefined,
          minutesBefore,
          channels: rule.channels,
          title,
          messageTemplate,
          button: normalizeLivestreamNotificationButton(rule.buttonText, rule.buttonUrl),
          localizedTemplates: Object.keys(localizedTemplates).length > 0 ? localizedTemplates : null,
          isActive: rule.isActive,
        };
      });

      saveMutation.mutate({
        url: `/admin/livestreams/${livestream.id}/notifications`,
        method: 'put',
        values: { rules: normalizedRules },
      }, {
        onSuccess: () => {
          notify.success('Настройки уведомлений сохранены');
          settingsQuery.query.refetch();
        },
        onError: (error) => notify.error(error.message || 'Не удалось сохранить уведомления'),
      });
    } catch (error) {
      notify.error(error instanceof Error ? error.message : 'Проверьте настройки уведомлений');
    }
  };

  const subscribersCount = settings?.subscribersCount ?? 0;
  const subscribers = settings?.subscribers ?? [];

  return (
    <Card className="overflow-hidden">
      <CardHeader className="border-b pb-4">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div className="flex flex-col gap-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Bell className="h-4 w-4" />
              Уведомления и рассылка
            </CardTitle>
            <CardDescription>
              Пользователь включает напоминание на платформе, а здесь задаётся, когда и через какие каналы отправить сообщение.
            </CardDescription>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end">
            <LivestreamNotificationSubscribersDialog
              subscribers={subscribers}
              subscribersCount={subscribersCount}
            />
            <div className="rounded-lg border bg-background px-3 py-2">
              <div className="text-[11px] uppercase tracking-normal text-muted-foreground">Старт</div>
              <div className="text-sm font-medium">{formatDateTime(livestream.scheduledAt)}</div>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 pt-4">
        {!isScheduled && (
          <div className="rounded-lg border border-dashed bg-muted/30 p-4 text-sm text-muted-foreground">
            Уведомления редактируются только пока трансляция запланирована. После старта настройки фиксируются.
          </div>
        )}

        {settingsQuery.query.isLoading ? (
          <div className="flex min-h-48 items-center justify-center rounded-lg border text-sm text-muted-foreground">
            <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
            Загружаем уведомления...
          </div>
        ) : (
          <div className="space-y-3">
            {rules.map((rule, index) => {
              const sentLabel = rule.sentAt ? `Отправлено ${formatDateTime(rule.sentAt)}` : null;
              const channelsLabel = getLivestreamNotificationChannelsLabel(rule.channels);
              const activeSelectId = `livestream-${livestream.id}-notification-${rule.localId}-active`;
              const minutesBeforeInputId = `livestream-${livestream.id}-notification-${rule.localId}-minutes`;
              const titleInputId = `livestream-${livestream.id}-notification-${rule.localId}-title`;
              const ruleTitle = rule.title.trim() || `Уведомление #${index + 1}`;
              const isExpanded = !collapsedRuleIds.includes(rule.localId);

              return (
                <div key={rule.localId} className="rounded-lg border bg-background p-4">
                  <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex min-w-0 flex-col gap-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-sm font-semibold">{ruleTitle}</h3>
                        <Badge variant={rule.isActive ? 'default' : 'secondary'}>
                          {rule.isActive ? 'Активно' : 'Выключено'}
                        </Badge>
                        {sentLabel && <Badge variant="outline">{sentLabel}</Badge>}
                        {rule.lastError && <Badge variant="destructive">Ошибка отправки</Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {channelsLabel || 'Каналы не выбраны'} · {formatMinutesBefore(rule.minutesBefore)} до старта
                      </p>
                    </div>
                    <div className="flex items-center gap-2 self-start">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => toggleRuleCollapsed(rule.localId)}
                      >
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        {isExpanded ? 'Скрыть' : 'Раскрыть'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeRule(rule.localId)}
                        disabled={!isScheduled || isSaving || rules.length <= 1}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                        Удалить
                      </Button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="grid gap-4 xl:grid-cols-[220px_minmax(0,1fr)]">
                      <div className="space-y-3">
                        <div className="space-y-1.5">
                          <label htmlFor={minutesBeforeInputId} className="text-xs font-medium text-muted-foreground">
                            Время до старта
                          </label>
                          {MINUTES_BEFORE_PRESET_VALUES.has(rule.minutesBefore) ? (
                            <Select
                              value={rule.minutesBefore}
                              onValueChange={(value) => {
                                if (value === 'custom') {
                                  updateRule(rule.localId, (current) => ({
                                    ...current,
                                    minutesBefore: '',
                                  }));
                                } else {
                                  updateRule(rule.localId, (current) => ({
                                    ...current,
                                    minutesBefore: value,
                                  }));
                                }
                              }}
                              disabled={!isScheduled || isSaving}
                            >
                              <SelectTrigger id={minutesBeforeInputId} className="w-full">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {MINUTES_BEFORE_PRESETS.map((preset) => (
                                  <SelectItem key={preset.value} value={preset.value}>
                                    {preset.label}
                                  </SelectItem>
                                ))}
                                <SelectItem value="custom">Другое...</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <div className="space-y-2">
                              <div className="grid grid-cols-3 gap-1.5">
                                <div>
                                  <label className="text-[10px] text-muted-foreground">Дней</label>
                                  <Input
                                    type="number"
                                    min={0}
                                    max={7}
                                    value={Math.floor(Number(rule.minutesBefore || 0) / 1440)}
                                    onChange={(event) => {
                                      const totalMinutes = Number(rule.minutesBefore || 0);
                                      const currentDays = Math.floor(totalMinutes / 1440);
                                      const remainder = totalMinutes - currentDays * 1440;
                                      const newDays = Math.max(0, Math.min(7, Number(event.target.value) || 0));
                                      updateRule(rule.localId, (current) => ({
                                        ...current,
                                        minutesBefore: String(newDays * 1440 + remainder),
                                      }));
                                    }}
                                    disabled={!isScheduled || isSaving}
                                  />
                                </div>
                                <div>
                                  <label className="text-[10px] text-muted-foreground">Часов</label>
                                  <Input
                                    type="number"
                                    min={0}
                                    max={23}
                                    value={Math.floor((Number(rule.minutesBefore || 0) % 1440) / 60)}
                                    onChange={(event) => {
                                      const totalMinutes = Number(rule.minutesBefore || 0);
                                      const days = Math.floor(totalMinutes / 1440);
                                      const currentHours = Math.floor((totalMinutes % 1440) / 60);
                                      const mins = totalMinutes - days * 1440 - currentHours * 60;
                                      const newHours = Math.max(0, Math.min(23, Number(event.target.value) || 0));
                                      updateRule(rule.localId, (current) => ({
                                        ...current,
                                        minutesBefore: String(days * 1440 + newHours * 60 + mins),
                                      }));
                                    }}
                                    disabled={!isScheduled || isSaving}
                                  />
                                </div>
                                <div>
                                  <label className="text-[10px] text-muted-foreground">Минут</label>
                                  <Input
                                    id={minutesBeforeInputId}
                                    type="number"
                                    min={0}
                                    max={59}
                                    value={Number(rule.minutesBefore || 0) % 60}
                                    onChange={(event) => {
                                      const totalMinutes = Number(rule.minutesBefore || 0);
                                      const daysAndHours = totalMinutes - (totalMinutes % 60);
                                      const newMins = Math.max(0, Math.min(59, Number(event.target.value) || 0));
                                      updateRule(rule.localId, (current) => ({
                                        ...current,
                                        minutesBefore: String(daysAndHours + newMins),
                                      }));
                                    }}
                                    disabled={!isScheduled || isSaving}
                                  />
                                </div>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-6 px-2 text-xs text-muted-foreground"
                                onClick={() => updateRule(rule.localId, (current) => ({
                                  ...current,
                                  minutesBefore: '15',
                                }))}
                                disabled={!isScheduled || isSaving}
                              >
                                Назад к пресетам
                              </Button>
                            </div>
                          )}
                          <p className="text-[11px] text-muted-foreground">
                            {formatMinutesBefore(rule.minutesBefore)} до старта
                          </p>
                        </div>

                      <div className="space-y-2">
                        <div className="text-xs font-medium text-muted-foreground">Каналы</div>
                        <div className="grid gap-2">
                          {LIVESTREAM_NOTIFICATION_CHANNEL_OPTIONS.map((option) => {
                            const checkboxId = `livestream-${livestream.id}-notification-${rule.localId}-${option.value}`;
                            return (
                              <label
                                key={option.value}
                                htmlFor={checkboxId}
                                className="flex cursor-pointer items-center gap-2 rounded-lg border bg-muted/20 px-3 py-2 text-sm"
                              >
                                <Checkbox
                                  id={checkboxId}
                                  checked={rule.channels.includes(option.value)}
                                  onCheckedChange={() => toggleChannel(rule.localId, option.value)}
                                  disabled={!isScheduled || isSaving}
                                />
                                {option.icon}
                                {option.label}
                              </label>
                            );
                          })}
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label htmlFor={activeSelectId} className="text-xs font-medium text-muted-foreground">
                          Статус отправки
                        </label>
                        <Select
                          value={rule.isActive ? 'active' : 'inactive'}
                          onValueChange={(value) => updateRule(rule.localId, (current) => ({
                            ...current,
                            isActive: value === 'active',
                          }))}
                          disabled={!isScheduled || isSaving}
                        >
                          <SelectTrigger id={activeSelectId} className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">Отправлять</SelectItem>
                            <SelectItem value="inactive">Не отправлять</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          Если выключить, правило останется в списке, но не сработает.
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <Tabs defaultValue="ru">
                        <TabsList className="mb-2 h-8">
                          <TabsTrigger value="ru" className="text-xs px-3 py-1">RU (основной)</TabsTrigger>
                          {NOTIFICATION_LANGUAGES.filter((lang) => lang.code !== 'ru').map((lang) => {
                            const tpl = rule.localizedTemplates[lang.code];
                            const filled = tpl && tpl.title.trim() && tpl.messageTemplate.trim();
                            return (
                              <TabsTrigger key={lang.code} value={lang.code} className="text-xs px-3 py-1">
                                {lang.code.toUpperCase()}
                                {filled && <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-green-500" />}
                              </TabsTrigger>
                            );
                          })}
                        </TabsList>
                        <TabsContent value="ru" className="space-y-3 mt-0">
                          <div className="space-y-1.5">
                            <label htmlFor={titleInputId} className="text-xs font-medium text-muted-foreground">
                              Название уведомления
                            </label>
                            <Input
                              id={titleInputId}
                              value={rule.title}
                              onChange={(event) => updateRule(rule.localId, (current) => ({
                                ...current,
                                title: event.target.value,
                              }))}
                              placeholder="Заголовок уведомления"
                              disabled={!isScheduled || isSaving}
                            />
                          </div>
                          <Textarea
                            value={rule.messageTemplate}
                            onChange={(event) => updateRule(rule.localId, (current) => ({
                              ...current,
                              messageTemplate: event.target.value,
                            }))}
                            placeholder="Текст уведомления"
                            className="min-h-36 resize-y"
                            disabled={!isScheduled || isSaving}
                          />
                          <div className="grid gap-3 md:grid-cols-[220px_minmax(0,1fr)]">
                            <Input
                              value={rule.buttonText}
                              onChange={(event) => updateRule(rule.localId, (current) => ({
                                ...current,
                                buttonText: event.target.value,
                              }))}
                              placeholder="Текст кнопки"
                              disabled={!isScheduled || isSaving}
                            />
                            <Input
                              value={rule.buttonUrl}
                              onChange={(event) => updateRule(rule.localId, (current) => ({
                                ...current,
                                buttonUrl: event.target.value,
                              }))}
                              placeholder="Ссылка кнопки, если нужна своя"
                              disabled={!isScheduled || isSaving}
                            />
                          </div>
                        </TabsContent>
                        {NOTIFICATION_LANGUAGES.filter((lang) => lang.code !== 'ru').map((lang) => {
                          const tpl = rule.localizedTemplates[lang.code] ?? { title: '', messageTemplate: '', buttonText: '', buttonUrl: '' };
                          return (
                            <TabsContent key={lang.code} value={lang.code} className="space-y-3 mt-0">
                              <p className="text-xs text-muted-foreground">
                                Перевод на {lang.label}. Если оставить пустым, будет использован основной (RU) текст.
                              </p>
                              <div className="space-y-1.5">
                                <label className="text-xs font-medium text-muted-foreground">
                                  Название ({lang.code.toUpperCase()})
                                </label>
                                <Input
                                  value={tpl.title}
                                  onChange={(event) => updateLocalizedField(rule.localId, lang.code, 'title', event.target.value)}
                                  placeholder={rule.title || 'Заголовок уведомления'}
                                  disabled={!isScheduled || isSaving}
                                />
                              </div>
                              <Textarea
                                value={tpl.messageTemplate}
                                onChange={(event) => updateLocalizedField(rule.localId, lang.code, 'messageTemplate', event.target.value)}
                                placeholder={rule.messageTemplate || 'Текст уведомления'}
                                className="min-h-36 resize-y"
                                disabled={!isScheduled || isSaving}
                              />
                              <div className="grid gap-3 md:grid-cols-[220px_minmax(0,1fr)]">
                                <Input
                                  value={tpl.buttonText}
                                  onChange={(event) => updateLocalizedField(rule.localId, lang.code, 'buttonText', event.target.value)}
                                  placeholder={rule.buttonText || 'Текст кнопки'}
                                  disabled={!isScheduled || isSaving}
                                />
                                <Input
                                  value={tpl.buttonUrl}
                                  onChange={(event) => updateLocalizedField(rule.localId, lang.code, 'buttonUrl', event.target.value)}
                                  placeholder={rule.buttonUrl || 'Ссылка кнопки'}
                                  disabled={!isScheduled || isSaving}
                                />
                              </div>
                            </TabsContent>
                          );
                        })}
                      </Tabs>
                      {rule.lastError && (
                        <p className="rounded-md bg-destructive/10 p-2 text-xs text-destructive">
                          {rule.lastError}
                        </p>
                      )}
                    </div>
                  </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={addRule}
            disabled={!isScheduled || isSaving}
          >
            <Plus className="h-4 w-4" />
            Добавить уведомление
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!isScheduled || isSaving || settingsQuery.query.isLoading}
          >
            {isSaving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Сохранить уведомления
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function AnalyticsSummaryCard({
  icon,
  label,
  value,
  hint,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-xs font-normal text-muted-foreground">
          {icon}
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-2xl font-semibold tabular-nums">{value}</p>
        <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
      </CardContent>
    </Card>
  );
}

/**
 * Карточка «Ёмкость сети по зрителям». Информационная метрика: показывает
 * текущую исходящую нагрузку сервера и сколько ещё зрителей выдержит
 * свободная полоса при текущем битрейте. Никого не блокирует.
 *
 * На не-Linux окружении (локальная разработка на macOS) сетевые метрики
 * недоступны — показываем «н/д».
 */
function NetworkCapacityCard({
  capacity,
}: {
  capacity: LivestreamNetworkCapacity | undefined;
}) {
  if (!capacity || !capacity.available) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <Gauge className="h-4 w-4" />
            Ёмкость сети по зрителям
          </CardTitle>
          <CardDescription>
            Сетевые метрики недоступны в этом окружении (требуется Linux-хост
            с доступом к /proc/net/dev и /sys/class/net).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">н/д</p>
        </CardContent>
      </Card>
    );
  }

  const capacityViewers = capacity.capacityViewers ?? 0;
  const activeViewers = capacity.activeViewers;
  const headroom = Math.max(0, capacityViewers - activeViewers);
  const atRisk = capacityViewers <= activeViewers;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium">
          <Gauge className="h-4 w-4" />
          Ёмкость сети по зрителям
        </CardTitle>
        <CardDescription>
          Расчёт по свободной полосе интерфейса
          {capacity.iface ? ` ${capacity.iface}` : ''} и битрейту{' '}
          {capacity.bitratePerViewerMbps ?? 0} Мбит/с на зрителя
        </CardDescription>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2 xl:grid-cols-4">
        <div className="rounded-md border p-3">
          <p className="text-xs text-muted-foreground">Сейчас выдержит</p>
          <p
            className={`mt-1 text-2xl font-semibold tabular-nums ${
              atRisk ? 'text-destructive' : ''
            }`}
          >
            {formatNumber(capacityViewers)}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">зрителей</p>
        </div>
        <div className="rounded-md border p-3">
          <p className="text-xs text-muted-foreground">Сейчас смотрят</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">
            {formatNumber(activeViewers)}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {atRisk ? 'сеть на пределе' : `запас ${formatNumber(headroom)}`}
          </p>
        </div>
        <div className="rounded-md border p-3">
          <p className="text-xs text-muted-foreground">Свободная полоса</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">
            {capacity.freeMbps ?? 0}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Мбит/с из {capacity.nicSpeedMbps ?? 0}
          </p>
        </div>
        <div className="rounded-md border p-3">
          <p className="text-xs text-muted-foreground">Текущая отдача</p>
          <p className="mt-1 text-2xl font-semibold tabular-nums">
            {capacity.currentTxMbps ?? 0}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">Мбит/с (tx)</p>
        </div>
      </CardContent>
    </Card>
  );
}

const LIVESTREAM_ANALYTICS_LABELS: Record<keyof LivestreamAnalyticsBucket, string> = {
  bucketStart: 'Начало бакета',
  bucketEnd: 'Конец бакета',
  activeViewers: 'Онлайн',
  joins: 'Входы',
  leaves: 'Выходы',
  viewerSeconds: 'Секунды просмотра',
  estimatedTrafficMb: 'Расчётный трафик',
  chatMessages: 'Сообщения',
  viewerMessages: 'Сообщения зрителей',
  adminMessages: 'Сообщения администраторов',
  interactionResponses: 'Ответы',
  moderationActions: 'Модерация',
};

function formatAnalyticsTooltipValue(
  key: keyof LivestreamAnalyticsBucket,
  value: number,
): string {
  if (key === 'estimatedTrafficMb') {
    return formatMegabytes(value);
  }
  if (key === 'viewerSeconds') {
    return `${formatNumber(value)} сек.`;
  }
  return formatNumber(value);
}

function LivestreamAnalyticsTooltip({
  active,
  payload,
  label,
  data,
}: LivestreamAnalyticsTooltipProps) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  const point = data.find((item) => item.label === label);
  return (
    <div className="min-w-48 rounded-md border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-lg">
      <p className="mb-1 font-medium">
        {point ? formatShortDateTime(point.bucketStart) : label}
      </p>
      <div className="space-y-1">
        {payload.map((entry) => (
          <div key={entry.dataKey} className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: entry.color ?? 'currentColor' }}
              />
              {entry.name ?? LIVESTREAM_ANALYTICS_LABELS[entry.dataKey]}
            </span>
            <span className="font-medium text-foreground tabular-nums">
              {formatAnalyticsTooltipValue(entry.dataKey, entry.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function LivestreamAnalyticsPanel({ livestreamId }: { livestreamId: number }) {
  const { query } = useCustom<LivestreamAnalyticsResponse>({
    url: `/admin/livestreams/${livestreamId}/analytics`,
    method: 'get',
    queryOptions: {
      // Реалтайм: cron шлёт livestream.analytics.tick / media.runtime каждые 4с.
      // refetchInterval — safety-net на случай дропа WS.
      refetchInterval: 5 * 60_000,
    },
  });

  const analytics = query.data?.data;
  const isLoading = query.isLoading || query.isFetching;

  const { query: capacityQuery } = useCustom<LivestreamNetworkCapacity>({
    url: `/admin/livestreams/${livestreamId}/network-capacity`,
    method: 'get',
    queryOptions: {
      // Реалтайм: livestream.analytics.tick тянет за собой /network-capacity.
      refetchInterval: 5 * 60_000,
    },
  });
  const capacity = capacityQuery.data?.data;

  const chartData = useMemo<LivestreamAnalyticsChartPoint[]>(() => {
    return (analytics?.buckets ?? []).map((bucket) => ({
      ...bucket,
      label: formatShortDateTime(bucket.bucketStart),
    }));
  }, [analytics?.buckets]);

  if (isLoading && !analytics) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        Загрузка аналитики...
      </div>
    );
  }

  if (!analytics || chartData.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          Пока нет данных аналитики по этой трансляции.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <AnalyticsSummaryCard
          icon={<Users className="h-4 w-4" />}
          label="Пик зрителей"
          value={formatNumber(analytics.summary.peakViewers)}
          hint={`Уникальных: ${formatNumber(analytics.summary.totalUniqueViewers)}`}
        />
        <AnalyticsSummaryCard
          icon={<Activity className="h-4 w-4" />}
          label="Средний онлайн"
          value={formatNumber(analytics.summary.averageViewers)}
          hint={`Интервал: ${analytics.bucketMinutes} мин.`}
        />
        <AnalyticsSummaryCard
          icon={<MessageSquareText className="h-4 w-4" />}
          label="Чат"
          value={formatNumber(analytics.summary.totalChatMessages)}
          hint={`${formatNumber(analytics.summary.chatMessagesPerViewerHour)} сообщений / зритель-час`}
        />
        <AnalyticsSummaryCard
          icon={<Wifi className="h-4 w-4" />}
          label="Расчётный трафик"
          value={formatMegabytes(analytics.summary.totalEstimatedTrafficMb)}
          hint={`По ${analytics.averageBitrateMbps} Мбит/с на секунду просмотра`}
        />
      </div>

      <NetworkCapacityCard capacity={capacity} />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <RadioTower className="h-4 w-4" />
            Техническое состояние MediaMTX
          </CardTitle>
          <CardDescription>
            Текущий снимок из контейнера MediaMTX на момент {formatShortDateTime(analytics.mediaRuntime.snapshotAt)}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 text-sm md:grid-cols-2 xl:grid-cols-5">
          <div className="rounded-md border p-3">
            <p className="text-xs text-muted-foreground">Поток</p>
            <p className="mt-1 font-medium">
              {analytics.mediaRuntime.available ? 'активен' : 'не активен'}
            </p>
          </div>
          <div className="rounded-md border p-3">
            <p className="text-xs text-muted-foreground">Читатели MediaMTX</p>
            <p className="mt-1 font-medium tabular-nums">
              {formatNumber(analytics.mediaRuntime.activeReaders)}
            </p>
          </div>
          <div className="rounded-md border p-3">
            <p className="text-xs text-muted-foreground">Треки</p>
            <p className="mt-1 font-medium tabular-nums">
              {formatNumber(analytics.mediaRuntime.tracks)}
            </p>
          </div>
          <div className="rounded-md border p-3">
            <p className="text-xs text-muted-foreground">Получено контейнером</p>
            <p className="mt-1 font-medium tabular-nums">
              {formatBytes(analytics.mediaRuntime.bytesReceived)}
            </p>
          </div>
          <div className="rounded-md border p-3">
            <p className="text-xs text-muted-foreground">Отдано контейнером</p>
            <p className="mt-1 font-medium tabular-nums">
              {formatBytes(analytics.mediaRuntime.bytesSent)}
            </p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <Users className="h-4 w-4" />
              Онлайн и входы
            </CardTitle>
            <CardDescription>Активные зрители, подключения и выходы по времени</CardDescription>
          </CardHeader>
          <CardContent className="h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} />
                <YAxis tickLine={false} axisLine={false} fontSize={12} allowDecimals={false} />
                <Tooltip content={<LivestreamAnalyticsTooltip data={chartData} />} />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="activeViewers"
                  name="Онлайн"
                  stroke="#2563eb"
                  fill="#2563eb"
                  fillOpacity={0.18}
                />
                <Area
                  type="monotone"
                  dataKey="joins"
                  name="Входы"
                  stroke="#16a34a"
                  fill="#16a34a"
                  fillOpacity={0.12}
                />
                <Area
                  type="monotone"
                  dataKey="leaves"
                  name="Выходы"
                  stroke="#f97316"
                  fill="#f97316"
                  fillOpacity={0.1}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <MessageSquareText className="h-4 w-4" />
              Активность чата
            </CardTitle>
            <CardDescription>Сообщения зрителей, модераторов и модерационные действия</CardDescription>
          </CardHeader>
          <CardContent className="h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} />
                <YAxis tickLine={false} axisLine={false} fontSize={12} allowDecimals={false} />
                <Tooltip content={<LivestreamAnalyticsTooltip data={chartData} />} />
                <Legend />
                <Bar dataKey="viewerMessages" name="Зрители" stackId="chat" fill="#3b82f6" />
                <Bar dataKey="adminMessages" name="Администраторы" stackId="chat" fill="#a855f7" />
                <Bar dataKey="moderationActions" name="Модерация" fill="#ef4444" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <Gauge className="h-4 w-4" />
              Плотность просмотра
            </CardTitle>
            <CardDescription>Секунды просмотра показывают фактическую нагрузку аудитории по бакетам</CardDescription>
          </CardHeader>
          <CardContent className="h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} />
                <YAxis tickLine={false} axisLine={false} fontSize={12} allowDecimals={false} />
                <Tooltip content={<LivestreamAnalyticsTooltip data={chartData} />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="viewerSeconds"
                  name="Секунды просмотра"
                  stroke="#0ea5e9"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm font-medium">
              <Wifi className="h-4 w-4" />
              Расчётная плотность трафика
            </CardTitle>
            <CardDescription>Оценка по секундам просмотра и среднему битрейту {analytics.averageBitrateMbps} Мбит/с</CardDescription>
          </CardHeader>
          <CardContent className="h-[320px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} />
                <YAxis tickLine={false} axisLine={false} fontSize={12} allowDecimals={false} />
                <Tooltip content={<LivestreamAnalyticsTooltip data={chartData} />} />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="estimatedTrafficMb"
                  name="Трафик"
                  stroke="#14b8a6"
                  fill="#14b8a6"
                  fillOpacity={0.18}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-medium">
            <BarChart3 className="h-4 w-4" />
            Интерактивы
          </CardTitle>
          <CardDescription>Ответы на голосования и сбор вопросов по времени</CardDescription>
        </CardHeader>
        <CardContent className="h-[260px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} fontSize={12} />
              <YAxis tickLine={false} axisLine={false} fontSize={12} allowDecimals={false} />
              <Tooltip content={<LivestreamAnalyticsTooltip data={chartData} />} />
              <Bar dataKey="interactionResponses" name="Ответы" fill="#f59e0b" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}

export function LivestreamsShow() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const invalidate = useInvalidate();

  const { result: livestream, query } = useOne<Livestream>({
    resource: 'livestreams',
    id: id ?? '',
  });
  const isLoading = query?.isLoading ?? false;

  // Push-обновления страницы трансляции через /ws/notifications.
  // См. apps/backend/src/domains/livestreams/livestream.realtime.ts.
  useLivestreamRealtime(livestream?.id);

  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [confirmReschedule, setConfirmReschedule] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState<Date | null>(null);
  const [confirmEnd, setConfirmEnd] = useState(false);

  const { result: languagesResult } = useList<LanguageRecord>({
    resource: 'languages',
    pagination: { pageSize: 100, mode: 'server' },
  });
  const availableLanguages: TranslationLanguage[] = (languagesResult?.data ?? []).map((language) => ({
    code: language.code,
    name: language.name,
    nativeName: language.nativeName || language.name,
    flag: language.flag ?? language.flagEmoji ?? '🏳️',
  }));
  const effectiveLanguages: TranslationLanguage[] = availableLanguages.length > 0
    ? availableLanguages
    : [
      { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺' },
      { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
      { code: 'uz', name: 'Uzbek', nativeName: 'Oʻzbekcha', flag: '🇺🇿' },
    ];

  const { mutate: runCustomMutation, mutation: customMutation } = useCustomMutation();
  const updateMutation = useUpdate<Livestream>();
  const isMutating = customMutation.isPending;
  const isUpdatingLivestream = updateMutation.mutation?.isPending ?? false;

  const isLive = livestream?.status === LivestreamStatus.LIVE
    || livestream?.status === LivestreamStatus.RECONNECTING;

  const viewersQuery = useCustom<ActiveViewer[]>({
    url: isLive ? `/admin/livestreams/${id}/viewers` : '',
    method: 'get',
    queryOptions: {
      enabled: isLive,
      // Реалтайм: push livestream.viewers.changed (heartbeat/leave/join).
      // refetchInterval оставляем только как safety-net на случай дропа WS.
      refetchInterval: 5 * 60_000,
    },
  });
  const viewersLoading = viewersQuery.query.isLoading;
  const viewers = viewersQuery.result?.data ?? [];

  const [viewerMuteTarget, setViewerMuteTarget] = useState<ActiveViewer | null>(null);
  const [viewerBanTarget, setViewerBanTarget] = useState<ActiveViewer | null>(null);
  const [viewerMuteReason, setViewerMuteReason] = useState('');
  const [viewerBanReason, setViewerBanReason] = useState('');
  const { mutate: runMuteMutation, mutation: muteMutation } = useCustomMutation();
  const { mutate: runBanMutation, mutation: banMutation } = useCustomMutation();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        Загрузка...
      </div>
    );
  }

  if (!livestream) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        Трансляция не найдена
      </div>
    );
  }

  const invalidateCurrent = () => {
    invalidate({
      resource: 'livestreams',
      invalidates: ['detail', 'list'],
      id: livestream.id,
    });
  };

  const handleRegenerate = () => {
    runCustomMutation(
      {
        url: `/admin/livestreams/${livestream.id}/regenerate-key`,
        method: 'post',
        values: {},
      },
      {
        onSuccess: () => {
          notify.success('Stream key пересоздан');
          invalidateCurrent();
          setConfirmRegenerate(false);
        },
        onError: (error) => {
          notify.error(error.message || 'Ошибка пересоздания ключа');
        },
      },
    );
  };

  const handleCancel = () => {
    runCustomMutation(
      {
        url: `/admin/livestreams/${livestream.id}/cancel`,
        method: 'post',
        values: { reason: cancelReason.trim() || undefined },
      },
      {
        onSuccess: () => {
          notify.success('Трансляция отменена');
          invalidateCurrent();
          setConfirmCancel(false);
          setCancelReason('');
        },
        onError: (error) => {
          notify.error(error.message || 'Ошибка отмены');
        },
      },
    );
  };

  const handleReschedule = () => {
    if (!rescheduleDate) {
      notify.error('Укажите новую дату');
      return;
    }
    runCustomMutation(
      {
        url: `/admin/livestreams/${livestream.id}/reschedule`,
        method: 'post',
        values: { scheduledAt: rescheduleDate.toISOString() },
      },
      {
        onSuccess: () => {
          notify.success('Трансляция перенесена');
          invalidateCurrent();
          setConfirmReschedule(false);
          setRescheduleDate(null);
        },
        onError: (error) => {
          notify.error(error.message || 'Ошибка переноса');
        },
      },
    );
  };

  const handleEndStream = () => {
    runCustomMutation(
      {
        url: `/admin/livestreams/${livestream.id}/end`,
        method: 'post',
        values: {},
      },
      {
        onSuccess: () => {
          notify.success('Трансляция завершена, запись начнёт синхронизироваться');
          invalidateCurrent();
          setConfirmEnd(false);
        },
        onError: (error) => {
          notify.error(error.message || 'Ошибка завершения трансляции');
        },
      },
    );
  };

  const handleViewerMute = () => {
    if (!viewerMuteTarget || !livestream.chatRoomId) {return;}
    const reason = viewerMuteReason.trim();
    if (!reason) {
      notify.error('Укажите причину мьюта');
      return;
    }
    runMuteMutation(
      {
        url: `/admin/chat-moderation/users/${viewerMuteTarget.userId}/mute`,
        method: 'post',
        values: { chatRoomId: livestream.chatRoomId, reason, durationMinutes: 30 },
      },
      {
        onSuccess: () => {
          notify.success('Пользователь замьючен на 30 минут');
          setViewerMuteTarget(null);
          setViewerMuteReason('');
        },
        onError: (error) => {
          notify.error(error.message || 'Ошибка мьюта');
        },
      },
    );
  };

  const handleViewerBan = () => {
    if (!viewerBanTarget || !livestream.chatRoomId) {return;}
    const reason = viewerBanReason.trim();
    if (!reason) {
      notify.error('Укажите причину бана');
      return;
    }
    runBanMutation(
      {
        url: `/admin/chat-moderation/users/${viewerBanTarget.userId}/ban`,
        method: 'post',
        values: { chatRoomId: livestream.chatRoomId, reason },
      },
      {
        onSuccess: () => {
          notify.success('Пользователь забанен в чате трансляции');
          setViewerBanTarget(null);
          setViewerBanReason('');
        },
        onError: (error) => {
          notify.error(error.message || 'Ошибка бана');
        },
      },
    );
  };

  const handleInlineEdit = (values: LivestreamFormData): Promise<void> => {
    return new Promise((resolve, reject) => {
      updateMutation.mutate(
        {
          resource: 'livestreams',
          id: livestream.id,
          values: {
            visibility: values.visibility,
            scheduledAt: values.scheduledAt,
            recordingEnabled: values.recordingEnabled,
            maxViewersOverride: values.maxViewersOverride,
            thumbnailFileId: values.thumbnailFileId,
            languages: values.languages,
            translations: values.translations,
            audienceFilters:
              values.visibility === LivestreamVisibility.PRIVATE
                ? values.audienceFilters
                : null,
          },
        },
        {
          onSuccess: () => {
            invalidateCurrent();
            resolve();
          },
          onError: (error) => {
            reject(error);
          },
        },
      );
    });
  };

  const canCancel =
    livestream.status === LivestreamStatus.SCHEDULED ||
    livestream.status === LivestreamStatus.LIVE ||
    livestream.status === LivestreamStatus.RECONNECTING;
  const canReschedule = livestream.status === LivestreamStatus.SCHEDULED;
  const canRegenerateKey = livestream.status !== LivestreamStatus.LIVE;
  const canEdit = livestream.status === LivestreamStatus.SCHEDULED;
  // Ручное завершение доступно только после остановки OBS (publisher ушёл,
  // стрим в grace-периоде RECONNECTING) — бэкенд проверяет это же предусловие.
  const canEndManually = livestream.status === LivestreamStatus.RECONNECTING;

  const { title: displayTitle } = getLivestreamTitle(livestream);
  const displayDescription = getLivestreamDescription(livestream);

  return (
    <div className="space-y-6">
      <FormHeader
        title={displayTitle}
        titleAddon={(
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={livestream.status} />
            <Badge variant="outline">{LIVESTREAM_VISIBILITY_LABELS[livestream.visibility]}</Badge>
            {livestream.recordingEnabled && (
              <Badge variant="secondary" className="gap-1">
                <Film className="h-3 w-3" />
                Запись
              </Badge>
            )}
            {livestream.languages.map((code) => (
              <Badge key={code} variant="outline" className="text-xs uppercase">
                {code}
              </Badge>
            ))}
          </div>
        )}
        onBack={() => navigate('/livestreams')}
        actions={(
          <div className="flex items-center gap-2">
            {canReschedule && (
              <Button variant="outline" onClick={() => setConfirmReschedule(true)}>
                <CalendarClock className="h-4 w-4 mr-2" />
                Перенести
              </Button>
            )}
            {canEndManually && (
              <Button variant="default" onClick={() => setConfirmEnd(true)}>
                <Square className="h-4 w-4 mr-2" />
                Завершить стрим
              </Button>
            )}
            {canCancel && (
              <Button
                variant="outline"
                onClick={() => setConfirmCancel(true)}
                className="text-destructive hover:text-destructive"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Отменить
              </Button>
            )}
          </div>
        )}
        sticky
      />

      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general">Общая информация</TabsTrigger>
          <TabsTrigger value="stream-source">Поток трансляции</TabsTrigger>
          <TabsTrigger value="restream">Рестрим</TabsTrigger>
          <TabsTrigger value="translation">Перевод</TabsTrigger>
          <TabsTrigger value="analytics">Аналитика</TabsTrigger>
          <TabsTrigger value="viewers">
            Зрители{isLive && viewers.length > 0 && <span className="ml-1.5 text-xs opacity-70">({viewers.length})</span>}
          </TabsTrigger>
          <TabsTrigger value="audience">Аудитория</TabsTrigger>
          <TabsTrigger value="stage">Сцена</TabsTrigger>
          <TabsTrigger value="breakout">Залы</TabsTrigger>
          <TabsTrigger value="interactions">Интерактивы</TabsTrigger>
          <TabsTrigger value="notifications">Уведомления</TabsTrigger>
          <TabsTrigger value="chat">Чат</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-4 mt-4">
          <LivestreamsFormFields
            key={`inline-edit-${livestream.id}-${livestream.updatedAt}`}
            isEdit
            livestreamId={livestream.id}
            formLoading={isUpdatingLivestream}
            livestream={livestream}
            locked={!canEdit}
            availableLanguages={effectiveLanguages}
            onFinish={handleInlineEdit}
            onBack={() => {}}
            showHeader={false}
            showCancelAction={false}
            submitLabel="Сохранить общую информацию"
          />

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <RadioTower className="h-4 w-4" />
                Информация
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 text-sm">
                <div className="flex justify-between border-b pb-2">
                  <dt className="text-muted-foreground">ID</dt>
                  <dd className="font-mono">#{livestream.id}</dd>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <dt className="text-muted-foreground">Хост (admin ID)</dt>
                  <dd className="font-mono">#{livestream.hostAdminId}</dd>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <dt className="text-muted-foreground">Chat room</dt>
                  <dd className="font-mono">
                    {livestream.chatRoomId ? `#${livestream.chatRoomId}` : '—'}
                  </dd>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <dt className="text-muted-foreground">Запланирована</dt>
                  <dd>{formatDateTime(livestream.scheduledAt)}</dd>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <dt className="text-muted-foreground">Начата</dt>
                  <dd>{formatDateTime(livestream.startedAt)}</dd>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <dt className="text-muted-foreground">Завершена</dt>
                  <dd>{formatDateTime(livestream.endedAt)}</dd>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <dt className="text-muted-foreground">Создана</dt>
                  <dd>{formatDateTime(livestream.createdAt)}</dd>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <dt className="text-muted-foreground">Лимит зрителей</dt>
                  <dd>{livestream.maxViewersOverride ?? 'по умолчанию'}</dd>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <dt className="text-muted-foreground">Файл записи</dt>
                  <dd>
                    {livestream.recordingFileId
                      ? `file #${livestream.recordingFileId}`
                      : (livestream.recordingEnabled
                        ? 'запись в процессе / ожидается'
                        : 'не включена')}
                  </dd>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <dt className="text-muted-foreground">Привязка к уроку</dt>
                  <dd>
                    {livestream.linkedModuleItemId
                      ? `ModuleItem #${livestream.linkedModuleItemId}`
                      : 'нет'}
                  </dd>
                </div>
                <div className="flex justify-between border-b pb-2">
                  <dt className="text-muted-foreground">Thumbnail</dt>
                  <dd>
                    {livestream.thumbnailFileId
                      ? `file #${livestream.thumbnailFileId}`
                      : 'нет'}
                  </dd>
                </div>
              </dl>

              {livestream.linkedModuleItemId && livestream.recordingEnabled && (
                <div className="mt-4 p-3 rounded-md bg-yellow-500/10 border border-yellow-500/30 text-sm">
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 font-medium mb-1">Внимание: запись привязана к уроку</p>
                  <p className="text-muted-foreground">
                    Запись стрима будет сохранена как видео урока #{livestream.linkedModuleItemId}.
                    Удаление записи или самой трансляции отвяжет видео от урока.
                  </p>
                </div>
              )}

              {displayDescription && (
                <div className="mt-6">
                  <p className="text-xs text-muted-foreground mb-1">Описание</p>
                  <p className="text-sm whitespace-pre-wrap">{displayDescription}</p>
                </div>
              )}

              {livestream.cancelReason && (
                <div className="mt-6 p-3 rounded-md bg-destructive/10 text-sm">
                  <p className="text-xs text-destructive font-medium mb-1">Причина отмены</p>
                  <p>{livestream.cancelReason}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="stream-source" className="space-y-4 mt-4">
          <ObsDiagnosticsPanel
            livestreamId={livestream.id}
            livestreamStatus={livestream.status}
            testMode={livestream.testMode}
            onTestModeChange={() => { void invalidate({ resource: 'livestreams', invalidates: ['detail'], id: livestream.id }); }}
          />

          <StreamSourcePanel
            livestream={livestream}
            canRegenerateKey={canRegenerateKey}
            onRegenerate={() => setConfirmRegenerate(true)}
            copyToClipboard={copyToClipboard}
          />

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Инструкция для OBS</CardTitle>
              <CardDescription className="text-xs">
                Точные настройки. Их соблюдение убирает зависания у зрителей со слабым/мобильным интернетом.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <div className="text-sm font-semibold mb-2">1. Подключение (Settings → Stream)</div>
                <ul className="list-disc list-inside space-y-1.5 text-sm pl-1">
                  <li>Service: <code className="px-1 py-0.5 bg-muted rounded">Custom</code></li>
                  <li>
                    Server: <code className="px-1 py-0.5 bg-muted rounded">{DEFAULT_RTMP_URL}</code>
                  </li>
                  <li>
                    Stream Key: скопируйте кнопкой <Copy className="inline h-3 w-3" /> выше.
                  </li>
                </ul>
              </div>

              <div className="rounded-md border-2 border-amber-500/60 bg-amber-500/10 p-3">
                <div className="text-sm font-semibold mb-2 flex items-center gap-1.5 text-amber-700 dark:text-amber-400">
                  <RadioTower className="h-4 w-4" />
                  2. ВАЖНО: переключите Output Mode в Advanced
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  Settings → Output → в самом верху сменить{' '}
                  <code className="px-1 py-0.5 bg-background rounded">Output Mode</code> с{' '}
                  <code className="px-1 py-0.5 bg-background rounded">Simple</code> на{' '}
                  <code className="px-1 py-0.5 bg-background rounded">Advanced</code>. Без этого следующие
                  поля (Rate Control, B-frames, Profile, Tune) просто не появятся в интерфейсе OBS.
                </p>
              </div>

              <div className="rounded-md border-2 border-red-500/60 bg-red-500/10 p-3">
                <div className="text-sm font-semibold mb-2 flex items-center gap-1.5 text-red-700 dark:text-red-400">
                  <Gauge className="h-4 w-4" />
                  3. КРИТИЧНО: Output → вкладка Streaming (Video)
                </div>
                <p className="text-xs text-muted-foreground mb-2">
                  Эти параметры убирают «провисания каждые ~10 секунд» у зрителей. Ставить все, даже если
                  кажется лишним.
                </p>
                <ul className="space-y-1.5 text-sm pl-1">
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 dark:text-red-400 font-bold mt-0.5">●</span>
                    <span>
                      <b>Encoder:</b> <code className="px-1 py-0.5 bg-background rounded">x264</code>
                      {' '}(или NVENC / Apple VT H.264 — любой H.264, не HEVC и не AV1).
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 dark:text-red-400 font-bold mt-0.5">●</span>
                    <span>
                      <b>Rate Control:</b> <code className="px-1 py-0.5 bg-background rounded">CBR</code>
                      {' '}(строго, не VBR / не CRF — иначе MediaMTX делает «прыгающие» сегменты).
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 dark:text-red-400 font-bold mt-0.5">●</span>
                    <span>
                      <b>Bitrate:</b> <code className="px-1 py-0.5 bg-background rounded">4000–6000 Kbps</code>
                      {' '}(до 7000 максимум; больше — сервер принудительно отключит поток).
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 dark:text-red-400 font-bold mt-0.5">●</span>
                    <span>
                      <b>Keyframe Interval:</b> <code className="px-1 py-0.5 bg-background rounded">2</code>
                      {' '}(именно цифра 2 секунды, НЕ{' '}
                      <code className="px-1 py-0.5 bg-background rounded">0</code> и не{' '}
                      <code className="px-1 py-0.5 bg-background rounded">auto</code>).
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 dark:text-red-400 font-bold mt-0.5">●</span>
                    <span>
                      <b>B-frames:</b> <code className="px-1 py-0.5 bg-background rounded">0</code>
                      {' '}— устраняет ошибку{' '}
                      <code className="px-1 py-0.5 bg-background rounded">too many reordered frames</code>{' '}
                      на сервере, из-за которой плеер у зрителя сам пересоздаётся каждые ~10 сек.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 dark:text-red-400 font-bold mt-0.5">●</span>
                    <span>
                      <b>Profile:</b> <code className="px-1 py-0.5 bg-background rounded">main</code>
                      {' '}(или <code className="px-1 py-0.5 bg-background rounded">baseline</code> для
                      совместимости со старыми телефонами).
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 dark:text-red-400 font-bold mt-0.5">●</span>
                    <span>
                      <b>Tune:</b> <code className="px-1 py-0.5 bg-background rounded">zerolatency</code>
                      {' '}— даёт стабильную задержку и ровные сегменты.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 dark:text-red-400 font-bold mt-0.5">●</span>
                    <span>
                      <b>Preset (x264):</b>{' '}
                      <code className="px-1 py-0.5 bg-background rounded">veryfast</code>{' '}
                      — баланс качества и нагрузки на CPU.
                    </span>
                  </li>
                </ul>
              </div>

              <div>
                <div className="text-sm font-semibold mb-2">4. Settings → Video</div>
                <ul className="list-disc list-inside space-y-1.5 text-sm pl-1">
                  <li>
                    Output (Scaled) Resolution:{' '}
                    <code className="px-1 py-0.5 bg-muted rounded">1280x720</code>
                  </li>
                  <li>
                    FPS: <code className="px-1 py-0.5 bg-muted rounded">30</code>{' '}
                    (60 не нужно — увеличивает нагрузку на канал зрителя без видимой пользы для лекции).
                  </li>
                </ul>
              </div>

              <div>
                <div className="text-sm font-semibold mb-2">5. Output → вкладка Audio</div>
                <ul className="list-disc list-inside space-y-1.5 text-sm pl-1">
                  <li>
                    Audio Encoder: <code className="px-1 py-0.5 bg-muted rounded">AAC</code>
                    {' '}(не Opus — RTMP его не передаёт корректно → трек может
                    быть «skipped», у зрителя тишина без явной ошибки плеера).
                  </li>
                  <li>
                    Audio Bitrate: <code className="px-1 py-0.5 bg-muted rounded">128 Kbps</code>
                  </li>
                  <li>
                    Sample Rate: <code className="px-1 py-0.5 bg-muted rounded">48 kHz</code>
                    {' '}(или 44.1 kHz; экзотические rate → предупреждение в OBS Diagnostics).
                  </li>
                  <li>
                    Channels: <code className="px-1 py-0.5 bg-muted rounded">Stereo</code>
                    {' '}или Mono — не 5.1/surround (HLS часто ломает звук).
                  </li>
                </ul>
              </div>

              <div className="rounded-md border border-emerald-500/40 bg-emerald-500/5 p-3 space-y-2">
                <p className="text-xs flex items-start gap-1.5">
                  <Check className="h-3.5 w-3.5 mt-0.5 text-emerald-500 shrink-0" />
                  <span>
                    После смены настроек обязательно <b>перезапустить трансляцию в OBS</b>{' '}
                    (Stop Streaming → Start Streaming) — на лету параметры энкодера не применяются.
                  </span>
                </p>
                <p className="text-xs flex items-start gap-1.5">
                  <Check className="h-3.5 w-3.5 mt-0.5 text-emerald-500 shrink-0" />
                  <span>
                    Stream Key — секрет. Не публикуйте его и не передавайте третьим лицам.
                  </span>
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="restream" className="space-y-4 mt-4">
          <RestreamTab livestream={livestream} canEdit={canCancel} />
        </TabsContent>

        <TabsContent value="translation" className="space-y-4 mt-4">
          <TranslationTab livestreamId={livestream.id} canEdit={canCancel} />
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4 mt-4">
          <LivestreamAnalyticsPanel livestreamId={livestream.id} />
        </TabsContent>

        <TabsContent value="viewers" className="space-y-4 mt-4">
          <LivestreamViewersPanel livestreamId={livestream.id} />
          {!isLive && (
            <Card>
              <CardContent className="py-8 text-center text-xs text-muted-foreground">
                Список выше — все уникальные зрители за всё время. Действия модерации
                доступны только во время активной трансляции.
              </CardContent>
            </Card>
          )}
          {isLive && viewersLoading && (
            <div className="flex items-center justify-center py-16 text-muted-foreground">
              Загрузка зрителей...
            </div>
          )}
          {isLive && !viewersLoading && viewers.length === 0 && (
            <Card>
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                Сейчас нет активных зрителей.
              </CardContent>
            </Card>
          )}
          {isLive && !viewersLoading && viewers.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Активные зрители ({viewers.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Пользователь</TableHead>
                      <TableHead>Онлайн с</TableHead>
                      <TableHead>Время просмотра</TableHead>
                      <TableHead>Статус</TableHead>
                      <TableHead className="w-32">Действия</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {viewers.map((viewer) => {
                      const displayName = [viewer.firstName, viewer.lastName]
                        .filter(Boolean)
                        .join(' ') || (viewer.username ? `@${viewer.username}` : `#${viewer.userId}`);
                      const joinedAtStr = typeof viewer.joinedAt === 'string'
                        ? viewer.joinedAt
                        : viewer.joinedAt.toISOString();
                      return (
                        <TableRow key={viewer.userId}>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium text-sm">{displayName}</span>
                              {viewer.username && (
                                <span className="text-xs text-muted-foreground">@{viewer.username}</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {formatDateTime(joinedAtStr)}
                          </TableCell>
                          <TableCell className="font-mono text-sm">
                            {formatWatchTime(viewer.watchedSeconds)}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1 flex-wrap">
                              {viewer.isMuted && (
                                <Badge variant="secondary" className="text-xs gap-1">
                                  <VolumeX className="h-3 w-3" />
                                  Muted
                                </Badge>
                              )}
                              {viewer.isBanned && (
                                <Badge variant="destructive" className="text-xs gap-1">
                                  <Ban className="h-3 w-3" />
                                  Banned
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs"
                                disabled={viewer.isMuted || muteMutation.isPending}
                                onClick={() => setViewerMuteTarget(viewer)}
                              >
                                <VolumeX className="h-3 w-3 mr-1" />
                                Мьют
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs text-destructive hover:text-destructive"
                                disabled={viewer.isBanned || banMutation.isPending}
                                onClick={() => setViewerBanTarget(viewer)}
                              >
                                <Ban className="h-3 w-3 mr-1" />
                                Бан
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="audience" className="space-y-4 mt-4">
          <LivestreamAudiencePanel livestreamId={livestream.id} />
        </TabsContent>

        <TabsContent value="stage" className="space-y-4 mt-4">
          <LivestreamCallStage livestreamId={livestream.id} isLive={isLive} />
        </TabsContent>

        <TabsContent value="breakout" className="space-y-4 mt-4">
          <LivestreamBreakoutPanel livestreamId={livestream.id} isLive={isLive} />
        </TabsContent>

        <TabsContent value="interactions" className="mt-4">
          <InteractionsAdmin baseUrl={`/admin/livestreams/${livestream.id}`} />
        </TabsContent>

        <TabsContent value="notifications" className="mt-4">
          <LivestreamNotificationsAdmin livestream={livestream} />
        </TabsContent>

        <TabsContent value="chat" className="space-y-4 mt-4">
          <LivestreamChatModeration
            livestreamId={livestream.id}
            chatRoomId={livestream.chatRoomId}
          />
        </TabsContent>
      </Tabs>

      {/* Regenerate key confirm */}
      <AlertDialog open={confirmRegenerate} onOpenChange={setConfirmRegenerate}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Пересоздать stream key?</AlertDialogTitle>
            <AlertDialogDescription>
              Текущий ключ перестанет работать немедленно. OBS, запущенный на старом
              ключе, отключится. Действие нельзя отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isMutating}>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleRegenerate} disabled={isMutating}>
              Пересоздать
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel confirm */}
      <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Отменить трансляцию?</AlertDialogTitle>
            <AlertDialogDescription>
              Трансляция получит статус CANCELLED. Если сейчас идёт эфир, он будет
              завершён.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Причина отмены (опционально)"
              rows={3}
              maxLength={500}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isMutating}>Закрыть</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              disabled={isMutating}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Отменить трансляцию
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* End stream confirm */}
      <AlertDialog open={confirmEnd} onOpenChange={setConfirmEnd}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Завершить трансляцию?</AlertDialogTitle>
            <AlertDialogDescription>
              Трансляция получит статус ENDED. Если включена запись, она начнёт
              синхронизироваться с привязанным уроком. Доступно только после
              остановки OBS.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isMutating}>Закрыть</AlertDialogCancel>
            <AlertDialogAction onClick={handleEndStream} disabled={isMutating}>
              Завершить стрим
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reschedule confirm */}
      <AlertDialog open={confirmReschedule} onOpenChange={setConfirmReschedule}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Перенести трансляцию</AlertDialogTitle>
            <AlertDialogDescription>
              Укажите новую дату и время начала. Перенос возможен только для
              запланированных эфиров.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <DateTimePicker
              value={rescheduleDate}
              onChange={setRescheduleDate}
              placeholder="Новая дата и время"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isMutating}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReschedule}
              disabled={isMutating || !rescheduleDate}
            >
              Перенести
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Viewer mute confirm */}
      <AlertDialog open={!!viewerMuteTarget} onOpenChange={(open) => {
        if (!open) {
          setViewerMuteTarget(null);
          setViewerMuteReason('');
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Замьютить зрителя?</AlertDialogTitle>
            <AlertDialogDescription>
              Пользователь{viewerMuteTarget?.firstName ? ` ${viewerMuteTarget.firstName}` : ''}
              {viewerMuteTarget?.username ? ` (@${viewerMuteTarget.username})` : ''} будет
              замьючен в чате трансляции на 30 минут.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Textarea
              value={viewerMuteReason}
              onChange={(e) => setViewerMuteReason(e.target.value)}
              placeholder="Причина мьюта"
              rows={3}
              maxLength={500}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={muteMutation.isPending}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleViewerMute}
              disabled={muteMutation.isPending || !viewerMuteReason.trim()}
            >
              Замьютить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Viewer ban confirm */}
      <AlertDialog open={!!viewerBanTarget} onOpenChange={(open) => {
        if (!open) {
          setViewerBanTarget(null);
          setViewerBanReason('');
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Забанить зрителя?</AlertDialogTitle>
            <AlertDialogDescription>
              Пользователь{viewerBanTarget?.firstName ? ` ${viewerBanTarget.firstName}` : ''}
              {viewerBanTarget?.username ? ` (@${viewerBanTarget.username})` : ''} будет
              заблокирован в чате трансляции.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            <Textarea
              value={viewerBanReason}
              onChange={(e) => setViewerBanReason(e.target.value)}
              placeholder="Причина бана"
              rows={3}
              maxLength={500}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={banMutation.isPending}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleViewerBan}
              disabled={banMutation.isPending || !viewerBanReason.trim()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Забанить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function LivestreamChatOverlay() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { result: livestream, query } = useOne<Livestream>({
    resource: 'livestreams',
    id: id ?? '',
    queryOptions: {
      enabled: !!id,
    },
  });

  if (query.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-foreground">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Загружаем чат...
        </div>
      </div>
    );
  }

  if (!livestream) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6 text-foreground">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-base">Трансляция не найдена</CardTitle>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => navigate('/livestreams')}>
              Вернуться к трансляциям
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-3 text-foreground">
      <LivestreamChatModeration
        livestreamId={livestream.id}
        chatRoomId={livestream.chatRoomId}
        overlay
      />
    </div>
  );
}
